import {
  COLOR_MODES,
  DESIGN_VARIANTS,
  colorModeSchema,
  designVariantSchema,
  type ColorMode,
  type DesignVariant,
} from '@gb-ticket/shared-types';

/** One storage record. Do not add a second key for mode or variant. */
export const APPEARANCE_STORAGE_KEY = 'gb.appearance.v1';

export const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

export { COLOR_MODES, DESIGN_VARIANTS, type ColorMode, type DesignVariant };

export type Appearance = {
  colorMode: ColorMode;
  designVariant: DesignVariant;
};

export const DEFAULT_APPEARANCE: Appearance = {
  colorMode: 'system',
  designVariant: 'brand',
};

export function isColorMode(value: unknown): value is ColorMode {
  return colorModeSchema.safeParse(value).success;
}

export function isDesignVariant(value: unknown): value is DesignVariant {
  return designVariantSchema.safeParse(value).success;
}

/** Drops invalid fields. Missing or foreign values fall back per field. */
/** A personal value wins. An empty personal value follows the tenant, then the product start. */
export function resolveAppearance(
  profile: { colorMode: ColorMode | null; designVariant: DesignVariant | null } | null | undefined,
  brand: Appearance | null | undefined,
): Appearance {
  return {
    colorMode: profile?.colorMode ?? brand?.colorMode ?? DEFAULT_APPEARANCE.colorMode,
    designVariant:
      profile?.designVariant ?? brand?.designVariant ?? DEFAULT_APPEARANCE.designVariant,
  };
}

export function parseAppearance(value: unknown): Appearance {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ...DEFAULT_APPEARANCE };
  }
  const record = value as { colorMode?: unknown; designVariant?: unknown };
  return {
    colorMode: isColorMode(record.colorMode) ? record.colorMode : DEFAULT_APPEARANCE.colorMode,
    designVariant: isDesignVariant(record.designVariant)
      ? record.designVariant
      : DEFAULT_APPEARANCE.designVariant,
  };
}
